import { supabase } from '../lib/supabase';

// Collections Service - CRUD operations for collections
export const collectionsService = {
  
  // Create a new collection
  async createCollection(collectionData) {
    try {
      const { data, error } = await supabase
        ?.from('collections')
        ?.insert({
          user_id: collectionData?.user_id,
          name: collectionData?.name,
          description: collectionData?.description || '',
          collectible_type: collectionData?.collectible_type,
          is_public: collectionData?.is_public || false,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating collection:', error);
      return {
        data: null,
        error: error?.message || 'Failed to create collection'
      };
    }
  },

  // Get collection overview for dashboard - the missing function!
  async getCollectionOverview(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // Fetch user's collections with aggregated collectible data
      const { data, error } = await supabase
        ?.from('collections')?.select(`*,collectibles:collectibles(id,name,current_value,primary_image_url,collectible_type,condition,created_at)`)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const getCollectionIcon = (type) => {
        switch (type) {
          case 'vintage-watches': return 'Watch';
          case 'sports-cards': return 'CreditCard';
          case 'comics': return 'Book';
          case 'art-prints': return 'Image';
          case 'stamps': return 'Mail';
          case 'rare-coins': return 'Coins';
          case 'sneakers': return 'ShoppingBag';
          case 'toys': return 'Gamepad2';
          default: return 'Package';
        }
      };

      // Transform the data to include calculated values
      const transformedCollections = data?.map(collection => {
        const totalValue = parseFloat(collection?.total_value) || 0;
        const itemCount = collection?.collectibles?.length || 0;
        // Calculate gain/loss as a percentage based on purchase vs current value
        const totalCost = collection?.collectibles?.reduce(
          (sum, item) => sum + (parseFloat(item?.purchase_price) || 0), 0
        ) || 0;
        const totalCurrent = collection?.collectibles?.reduce(
          (sum, item) => sum + (parseFloat(item?.current_value) || 0), 0
        ) || 0;
        const change = totalCost > 0
          ? parseFloat((((totalCurrent - totalCost) / totalCost) * 100)?.toFixed(2))
          : 0;

        return {
          ...collection,
          items: itemCount,
          value: totalValue || totalCurrent,
          change,
          icon: getCollectionIcon(collection?.collectible_type),
          recentItems: collection?.collectibles
            ?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))
            ?.slice(0, 3) || []
        };
      }) || [];

      return {
        data: transformedCollections,
        error: null
      };
    } catch (error) {
      console.error('Error fetching collection overview:', error);
      return {
        data: [],
        error: error?.message || 'Failed to fetch collection overview'
      };
    }
  },

  // Get public collections for news feed and featured content - NEW FUNCTION
  async getPublicCollections(limit = 10) {
    try {
      const { data, error } = await supabase
        ?.from('collections')
        ?.select(`
          id,
          name,
          description,
          collectible_type,
          total_value,
          item_count,
          created_at,
          updated_at,
          user_profiles(
            id,
            full_name,
            avatar_url,
            membership_tier
          ),
          collectibles(
            id,
            name,
            current_value,
            primary_image_url,
            collectible_type,
            condition
          )
        `)
        ?.eq('is_public', true)
        ?.order('total_value', { ascending: false })
        ?.limit(limit);

      if (error) {
        throw error;
      }

      // Transform the data to include calculated values and better structure
      const transformedCollections = data?.map(collection => ({
        id: collection?.id,
        name: collection?.name,
        description: collection?.description,
        collectible_type: collection?.collectible_type,
        total_value: collection?.total_value || 0,
        item_count: collection?.item_count || 0,
        created_at: collection?.created_at,
        updated_at: collection?.updated_at,
        owner: {
          id: collection?.user_profiles?.id,
          name: collection?.user_profiles?.full_name,
          avatar_url: collection?.user_profiles?.avatar_url,
          membership_tier: collection?.user_profiles?.membership_tier
        },
        featured_items: collection?.collectibles
          ?.sort((a, b) => (parseFloat(b?.current_value) || 0) - (parseFloat(a?.current_value) || 0))
          ?.slice(0, 3) || [],
        total_items: collection?.collectibles?.length || 0
      })) || [];

      return {
        data: transformedCollections,
        error: null
      };
    } catch (error) {
      console.error('Error fetching public collections:', error);
      return {
        data: [],
        error: error?.message || 'Failed to fetch public collections'
      };
    }
  },

  // Get all collections for admin overview
  async getAllCollections(options = {}) {
    try {
      const { 
        page = 1, 
        limit = 10, 
        search = '', 
        type = null 
      } = options;
      
      let query = supabase?.from('collections')?.select(`
        *,
        user_profiles(full_name, email)
      `, { count: 'exact' });

      // Apply search filter
      if (search) {
        query = query?.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      // Apply type filter
      if (type) {
        query = query?.eq('collectible_type', type);
      }

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query?.range(from, to);

      // Order by created date (newest first)
      query = query?.order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        throw error;
      }

      return {
        data: data || [],
        total: count || 0,
        page,
        limit,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw new Error('Failed to fetch collections');
    }
  },

  // Get collections statistics
  async getCollectionStats() {
    try {
      const [totalResult, publicResult, privateResult] = await Promise.all([
        supabase?.from('collections')?.select('id', { count: 'exact', head: true }),
        supabase?.from('collections')?.select('id', { count: 'exact', head: true })?.eq('is_public', true),
        supabase?.from('collections')?.select('id', { count: 'exact', head: true })?.eq('is_public', false)
      ]);

      return {
        totalCollections: totalResult?.count || 0,
        publicCollections: publicResult?.count || 0,
        privateCollections: privateResult?.count || 0
      };
    } catch (error) {
      console.error('Error fetching collection stats:', error);
      throw new Error('Failed to fetch collection statistics');
    }
  },

  // Get collections by type breakdown
  async getCollectionsByType() {
    try {
      const { data, error } = await supabase?.from('collections')
        ?.select('collectible_type, total_value')
        ?.order('total_value', { ascending: false });

      if (error) {
        throw error;
      }

      // Group by type and calculate totals
      const typeStats = {};
      data?.forEach(collection => {
        const type = collection?.collectible_type;
        if (!typeStats?.[type]) {
          typeStats[type] = {
            count: 0,
            totalValue: 0
          };
        }
        typeStats[type].count += 1;
        typeStats[type].totalValue += parseFloat(collection?.total_value) || 0;
      });

      return typeStats;
    } catch (error) {
      console.error('Error fetching collections by type:', error);
      throw new Error('Failed to fetch collections by type');
    }
  },

  // Get collection by ID (admin)
  async getCollectionById(id) {
    try {
      const { data, error } = await supabase?.from('collections')?.select(`
          *,
          user_profiles(full_name, email),
          collectibles(*)
        `)?.eq('id', id)?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching collection:', error);
      return { data: null, error: error?.message || 'Failed to fetch collection' };
    }
  },

  // Update collection (admin)
  async updateCollection(id, updates) {
    try {
      const { data, error } = await supabase?.from('collections')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating collection:', error);
      return { data: null, error: error?.message || 'Failed to update collection' };
    }
  },

  // Delete collection (admin)
  async deleteCollection(id) {
    try {
      const { error } = await supabase?.from('collections')?.delete()?.eq('id', id);

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      console.error('Error deleting collection:', error);
      return { error: error?.message || 'Failed to delete collection' };
    }
  },

  // Get user collections for a specific user
  async getUserCollections(userId) {
    try {
      const { data, error } = await supabase?.from('collections')?.select(`
          *,
          collectibles:collectibles(id, name, current_value, primary_image_url)
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Update collection sharing settings
  async updateSharingSettings(collectionId, isPublic, showcaseSettings) {
    try {
      const { data, error } = await supabase
        ?.from('collections')
        ?.update({
          is_public: isPublic,
          showcase_settings: showcaseSettings,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', collectionId)
        ?.select()
        ?.single();

      if (error) {
        throw error;
      }

      return {
        data,
        error: null
      };
    } catch (error) {
      console.error('Error updating sharing settings:', error);
      return {
        data: null,
        error: error?.message || 'Failed to update sharing settings'
      };
    }
  },

  // Get public collection by ID (no authentication required)
  async getPublicCollectionById(collectionId) {
    try {
      const { data, error } = await supabase
        ?.from('collections')
        ?.select(`
          id,
          name,
          description,
          collectible_type,
          is_public,
          total_value,
          item_count,
          showcase_settings,
          created_at,
          updated_at,
          user_profiles(
            id,
            full_name,
            avatar_url,
            membership_tier
          )
        `)
        ?.eq('id', collectionId)
        ?.eq('is_public', true)
        ?.single();

      if (error) {
        throw error;
      }

      return {
        data,
        error: null
      };
    } catch (error) {
      console.error('Error fetching public collection:', error);
      return {
        data: null,
        error: error?.message || 'Failed to fetch public collection'
      };
    }
  },

  // Get public collection items (no authentication required)
  async getPublicCollectionItems(collectionId) {
    try {
      const { data, error } = await supabase
        ?.from('collectibles')
        ?.select('*')
        ?.eq('collection_id', collectionId)
        ?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return {
        data: data || [],
        error: null
      };
    } catch (error) {
      console.error('Error fetching public collection items:', error);
      return {
        data: [],
        error: error?.message || 'Failed to fetch collection items'
      };
    }
  }
};

// Export the actual service as both named and default exports
export default collectionsService;

// Fix the capitalized export to reference the actual service instead of placeholder
export const CollectionsService = collectionsService;